import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Phone, Mail, Check, ArrowRight, Building, Video } from 'lucide-react';
const properties = ['Zylus Court Phase II', 'Tiwa Garden City', 'Lekki Pride III', 'Cityview Park', 'Park Lane Phase II', 'Not sure yet'];
export function BookInspection() {
  const [inspectionType, setInspectionType] = useState<'physical' | 'virtual'>('physical');
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3500);
  };
  return <section id="book" className="relative py-24 md:py-32 px-6 md:px-12 bg-ink text-white overflow-hidden" data-id="element-38">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gold/5 blur-[120px] pointer-events-none" data-id="element-39" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-gold/5 blur-[100px] pointer-events-none" data-id="element-40" />

      <div className="relative max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-41">
        {/* Left: Info */}
        <div className="lg:col-span-5 flex flex-col" data-id="element-42">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-xs font-semibold tracking-[0.2em] uppercase text-gold mb-6" data-id="element-43">
            Book Your Visit
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight mb-6 leading-[1.1]" data-id="element-44">
            Schedule a property inspection today.
          </motion.h2>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-white/60 text-lg leading-relaxed mb-12 max-w-md" data-id="element-45">
            Our property consultants will personally guide you through every
            detail. Choose between a physical visit or a virtual tour at your
            convenience.
          </motion.p>

          {/* Trust signals */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="space-y-4 mb-12" data-id="element-46">
            {['Free consultation, no obligations', 'Flexible scheduling 7 days a week', 'Expert guidance from licensed agents'].map((item, i) => <div key={i} className="flex items-center gap-3 text-white/80" data-id="element-47">
                <div className="w-6 h-6 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center shrink-0" data-id="element-48">
                  <Check className="w-3 h-3 text-gold" strokeWidth={3} data-id="element-49" />
                </div>
                <span className="text-sm" data-id="element-50">{item}</span>
              </div>)}
          </motion.div>

          {/* Contact details */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="mt-auto pt-8 border-t border-white/10 grid grid-cols-2 gap-6" data-id="element-51">
            <div data-id="element-52">
              <div className="flex items-center gap-2 text-gold mb-2" data-id="element-53">
                <Phone className="w-4 h-4" data-id="element-54" />
                <span className="text-xs uppercase tracking-widest font-semibold" data-id="element-55">
                  Call us
                </span>
              </div>
              <p className="text-white/90 text-sm" data-id="element-56">+234 800 000 0000</p>
            </div>
            <div data-id="element-57">
              <div className="flex items-center gap-2 text-gold mb-2" data-id="element-58">
                <MapPin className="w-4 h-4" data-id="element-59" />
                <span className="text-xs uppercase tracking-widest font-semibold" data-id="element-60">
                  Visit us
                </span>
              </div>
              <p className="text-white/90 text-sm" data-id="element-61">Lekki, Lagos, Nigeria</p>
            </div>
          </motion.div>
        </div>

        {/* Right: Form */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.2
      }} className="lg:col-span-7" data-id="element-62">
          <div className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-63">
            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-16" data-id="element-64">
                <div className="w-16 h-16 rounded-full bg-gold flex items-center justify-center mx-auto mb-6" data-id="element-65">
                  <Check className="w-8 h-8 text-white" strokeWidth={3} data-id="element-66" />
                </div>
                <h3 className="text-3xl font-serif mb-3" data-id="element-67">Booking received</h3>
                <p className="text-white/60" data-id="element-68">
                  Our team will contact you within 24 hours to confirm your
                  inspection.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-69">
                {/* Inspection Type */}
                <div data-id="element-70">
                  <label className="block text-xs font-semibold tracking-widest uppercase text-white/50 mb-3" data-id="element-71">
                    Inspection Type
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-72">
                    <button type="button" onClick={() => setInspectionType('physical')} className={`relative flex items-center gap-3 p-4 rounded-xl border transition-all ${inspectionType === 'physical' ? 'bg-gold/10 border-gold text-white' : 'border-white/10 hover:border-white/20 text-white/70'}`} data-id="element-73">
                      <Building className="w-5 h-5" data-id="element-74" />
                      <div className="text-left" data-id="element-75">
                        <div className="text-sm font-medium" data-id="element-76">Physical</div>
                        <div className="text-xs opacity-60" data-id="element-77">On-site visit</div>
                      </div>
                      {inspectionType === 'physical' && <motion.div layoutId="active-type" className="absolute top-3 right-3 w-2 h-2 rounded-full bg-gold" data-id="element-78" />}
                    </button>
                    <button type="button" onClick={() => setInspectionType('virtual')} className={`relative flex items-center gap-3 p-4 rounded-xl border transition-all ${inspectionType === 'virtual' ? 'bg-gold/10 border-gold text-white' : 'border-white/10 hover:border-white/20 text-white/70'}`} data-id="element-79">
                      <Video className="w-5 h-5" data-id="element-80" />
                      <div className="text-left" data-id="element-81">
                        <div className="text-sm font-medium" data-id="element-82">Virtual</div>
                        <div className="text-xs opacity-60" data-id="element-83">
                          Live video tour
                        </div>
                      </div>
                      {inspectionType === 'virtual' && <motion.div layoutId="active-type" className="absolute top-3 right-3 w-2 h-2 rounded-full bg-gold" data-id="element-84" />}
                    </button>
                  </div>
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-85">
                  <div data-id="element-86">
                    <label className="block text-xs font-semibold tracking-widest uppercase text-white/50 mb-2" data-id="element-87">
                      Full Name
                    </label>
                    <input type="text" placeholder="John Doe" required className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors" data-id="element-88" />
                  </div>
                  <div data-id="element-89">
                    <label className="block text-xs font-semibold tracking-widest uppercase text-white/50 mb-2" data-id="element-90">
                      Email
                    </label>
                    <input type="email" placeholder="you@email.com" required className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors" data-id="element-91" />
                  </div>
                </div>

                {/* Phone + Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-92">
                  <div data-id="element-93">
                    <label className="block text-xs font-semibold tracking-widest uppercase text-white/50 mb-2" data-id="element-94">
                      Phone
                    </label>
                    <input type="tel" placeholder="+234..." required className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors" data-id="element-95" />
                  </div>
                  <div data-id="element-96">
                    <label className="block text-xs font-semibold tracking-widest uppercase text-white/50 mb-2" data-id="element-97">
                      Preferred Date
                    </label>
                    <div className="relative" data-id="element-98">
                      <input type="date" required className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-gold transition-colors [color-scheme:dark]" data-id="element-99" />
                      <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" data-id="element-100" />
                    </div>
                  </div>
                </div>

                {/* Property */}
                <div data-id="element-101">
                  <label className="block text-xs font-semibold tracking-widest uppercase text-white/50 mb-2" data-id="element-102">
                    Property of Interest
                  </label>
                  <select required defaultValue="" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-103">
                    <option value="" disabled className="bg-ink" data-id="element-104">
                      Select a property
                    </option>
                    {properties.map(p => <option key={p} value={p} className="bg-ink" data-id="element-105">
                        {p}
                      </option>)}
                  </select>
                </div>

                {/* Message */}
                <div data-id="element-106">
                  <label className="block text-xs font-semibold tracking-widest uppercase text-white/50 mb-2" data-id="element-107">
                    Message{' '}
                    <span className="text-white/30 normal-case" data-id="element-108">
                      (optional)
                    </span>
                  </label>
                  <textarea rows={3} placeholder="Any specific requirements or questions..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors resize-none" data-id="element-109" />
                </div>

                {/* Submit */}
                <div className="flex items-center justify-between pt-2" data-id="element-110">
                  <p className="text-xs text-white/40 max-w-[200px]" data-id="element-111">
                    By submitting, you agree to our terms & privacy policy.
                  </p>
                  <button type="submit" className="bg-gold hover:bg-gold-light text-white font-medium px-6 py-3.5 rounded-xl transition-colors flex items-center gap-2 group" data-id="element-112">
                    Confirm Booking
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-113" />
                  </button>
                </div>
              </form>}
          </div>
        </motion.div>
      </div>
    </section>;
}